/**
 * Enregistrement produit — page PUBLIQUE, reprise de la version du client (itération 31).
 *
 * Le client final arrive ici par un lien personnel reçu après la signature. Il n'a pas
 * de compte : la page appelle donc `axios` directement, sans notre client d'API qui
 * pose un jeton d'authentification. C'est volontaire, et c'est ce qui distingue ce
 * fichier de tous les autres écrans.
 *
 * Sa protection tient au jeton de l'URL : 24 octets d'entropie, jamais listé, et un
 * jeton inconnu répond 404 sans dire pourquoi. Le serveur ne sert que ce que le client
 * connaît déjà — ses équipements, son opérateur — et jamais un identifiant interne.
 *
 * Seuls les chemins d'import changent par rapport à son fichier.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/shared/ui/button';
import { Card, CardContent } from '@/shared/ui/card';
import { CheckCircle2, ShieldCheck, BellRing, Headset, Sparkles, Loader2, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = { PAC: 'Pompe à chaleur', PV: 'Panneaux solaires', Ballon: 'Ballon électrique', Batterie: 'Batterie de stockage' };

export default function EnrollmentPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    axios.get(`${API}/enrollment/${token}`)
      .then(r => setData(r.data))
      .catch(err => setError(err.response?.data?.detail || 'Lien invalide ou expiré'))
      .finally(() => setLoading(false));
  }, [token]);

  const confirm = async () => {
    setConfirming(true);
    try {
      const r = await axios.post(`${API}/enrollment/${token}/confirm`);
      setData((d) => ({ ...d, status: 'confirmed', confirmed_at: r.data.confirmed_at }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Impossible de confirmer votre enregistrement');
    }
    setConfirming(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <XCircle className="w-14 h-14 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-neutral-900 mb-2">Lien invalide</h1>
            <p className="text-sm text-neutral-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const op = data.operator || {};
  const client = data.client || {};
  const products = data.products || [];
  const confirmed = data.status === 'confirmed';

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="enrollment-page">
      {/* Header opérateur */}
      <div className="bg-[var(--marque-accent)] px-6 py-5 text-white">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          {op.logo_url ? (
            <img src={op.logo_url} alt="" className="w-10 h-10 rounded object-contain bg-white p-1" />
          ) : (
            <div className="w-10 h-10 rounded bg-white/15 flex items-center justify-center text-white/70 text-lg font-bold">
              {(op.raison_sociale || op.name || 'O').charAt(0)}
            </div>
          )}
          <div>
            <p className="text-[11px] opacity-70 uppercase tracking-wide">Opérateur de flexibilité</p>
            <p className="text-[15px] font-semibold">{op.raison_sociale || op.name}</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4 md:p-8 space-y-6">
        {/* Titre */}
        <div className="text-center py-4">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 mb-4">
            <ShieldCheck className="w-7 h-7 text-emerald-600" />
          </div>
          <h1 className="text-2xl md:text-3xl font-semibold text-neutral-900 tracking-tight">
            {confirmed ? 'Enregistrement confirmé' : 'Activez votre garantie et vos services'}
          </h1>
          <p className="text-neutral-500 mt-2 text-sm">
            Bonjour {client.prenom} {client.nom}, {confirmed ? 'votre enregistrement officiel est actif.' : 'confirmez votre enregistrement officiel pour activer vos services.'}
          </p>
        </div>

        {/* Confirmation state */}
        {confirmed && (
          <Card className="border-emerald-200 bg-emerald-50">
            <CardContent className="p-5 flex items-start gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-emerald-900">Vos garanties et services sont activés</p>
                <p className="text-xs text-emerald-800 mt-1">
                  Confirmé le {data.confirmed_at ? new Date(data.confirmed_at).toLocaleString('fr-FR', {dateStyle:'long', timeStyle:'short'}) : '—'}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Produits enregistrés */}
        <Card>
          <CardContent className="p-5">
            <h2 className="text-sm font-semibold text-neutral-900 uppercase tracking-wide mb-4">
              Vos équipements ({products.length})
            </h2>
            <div className="space-y-3">
              {products.map((p, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-md border border-neutral-200 bg-white" data-testid={`enrollment-product-${i}`}>
                  <div className="w-10 h-10 rounded bg-neutral-100 flex items-center justify-center text-[10px] uppercase font-semibold text-neutral-500 shrink-0">
                    {p.type?.substring(0, 3)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-medium text-neutral-900">
                      {TYPE_LABELS[p.type] || p.type} — {p.marque || 'Marque non précisée'} {p.modele || ''}
                    </p>
                    <p className="text-[12px] text-neutral-500 mt-0.5">
                      {p.reference ? `Réf. ${p.reference}` : 'Sans référence'}
                      {p.puissance_kw ? ` · ${p.puissance_kw} kW` : ''}
                      {p.date_installation ? ` · Installé le ${new Date(p.date_installation).toLocaleDateString('fr-FR')}` : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Bénéfices */}
        <Card>
          <CardContent className="p-5">
            <h2 className="text-sm font-semibold text-neutral-900 uppercase tracking-wide mb-4">
              Vos avantages
            </h2>
            <div className="space-y-4">
              {[
                { Icon: ShieldCheck, title: "Garantie constructeur activée", desc: "Enregistrement officiel de votre installation auprès des fabricants." },
                { Icon: BellRing, title: "Alertes en cas d'anomalie", desc: "Nous vous notifions en cas de panne, dysfonctionnement ou consommation anormale." },
                { Icon: Headset, title: "Assistance à distance", desc: "Notre équipe technique peut identifier les anomalies à distance sans déplacement." },
                { Icon: Sparkles, title: "Optimisation énergétique", desc: "Nous ajustons le fonctionnement de vos équipements pour réduire vos consommations." },
              ].map(({ Icon, title, desc }, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-md bg-emerald-50 flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-[13.5px] font-medium text-neutral-900">{title}</p>
                    <p className="text-[12.5px] text-neutral-500 leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* CTA */}
        {!confirmed && (
          <div className="sticky bottom-0 -mx-4 md:mx-0 p-4 md:p-0 bg-neutral-50/95 backdrop-blur border-t md:border-0 border-neutral-200">
            <Button
              className="w-full h-12 text-[15px] font-semibold bg-[var(--marque-accent)] hover:brightness-125"
              onClick={confirm}
              disabled={confirming}
              data-testid="enrollment-confirm-btn"
            >
              {confirming ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Confirmation…</> : 'Confirmer mon enregistrement officiel'}
            </Button>
            <p className="text-[11px] text-neutral-400 text-center mt-2">
              En confirmant, vous acceptez d&apos;être contacté par {op.raison_sociale || op.name} pour la gestion de vos services.
            </p>
          </div>
        )}

        {/* Contact */}
        <p className="text-center text-[12px] text-neutral-400 pb-4">
          Une question ? Contactez-nous à{' '}
          {op.email_support ? <a href={`mailto:${op.email_support}`} className="text-neutral-600 hover:underline">{op.email_support}</a> : '—'}
        </p>
      </div>
    </div>
  );
}
